import { state } from '../state.js';
import { COMPARISON_DEFAULT_ENTITY_TYPES, type ComparisonChangeType, type ComparisonReport } from '../../comparison/types.js';
import type { EntityDiffChange, ComparisonEntityTypeViewState } from '../types.js';
import { escapeHtml, formatDateTime, formatRelativeTime } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';
import { executeBatchSyncJob, openBatchProgressModal } from './batch.controller.js';
import { openAddToVirtualTargetModal, formatDiffValue, getDiffTypeClass, getDiffTypeLabel } from './explorer.controller.js';

interface ComparisonTypeCount {
  entityType: string;
  added: number;
  removed: number;
  modified: number;
  unchanged: number;
  total: number;
}

interface ComparisonGroupedEntities {
  [entityType: string]: Array<{
    entityId: string;
    changeType: string;
    leftHash?: string | null;
    rightHash?: string | null;
  }>;
}

const POLL_INTERVAL_MS = 1500;

export const REPORT_FILTER_OPTIONS: Array<ComparisonChangeType | 'all'> = [
  'all',
  'added',
  'removed',
  'modified',
  'unchanged',
];

const CHANGE_TYPE_LEGEND: Array<{ key: ComparisonChangeType; symbol: string; label: string; cls: string }> = [
  { key: 'added', symbol: '+', label: 'Added', cls: 'count-added' },
  { key: 'removed', symbol: '−', label: 'Removed', cls: 'count-removed' },
  { key: 'modified', symbol: '~', label: 'Modified', cls: 'count-modified' },
  { key: 'unchanged', symbol: '=', label: 'Unchanged', cls: 'count-unchanged' },
];

function getChangeTypeSymbol(changeType: ComparisonChangeType | 'all'): string {
  switch (changeType) {
    case 'added':
      return '+';
    case 'removed':
      return '−';
    case 'modified':
      return '~';
    case 'unchanged':
      return '=';
    default:
      return '';
  }
}

function formatChangeTypeLabel(changeType: ComparisonChangeType | 'all' | 'actionable'): string {
  if (changeType === 'all') return 'All';
  if (changeType === 'actionable') return 'Actionable';
  return changeType.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isValidComparisonEntityTypeChangeFilter(
  value: string
): value is ComparisonChangeType | 'all' | 'actionable' {
  return value === 'all' || value === 'actionable' || REPORT_FILTER_OPTIONS.includes(value as ComparisonChangeType | 'all');
}

function escapeCssSelector(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/(["'\\])/g, '\\$1');
}

export function getDefaultComparisonEntityTypeViewState(): ComparisonEntityTypeViewState {
  return {
    page: 1,
    pageSize: 50,
    filter: '',
    changeFilter: 'actionable',
    showUnchanged: false,
  };
}

export function setComparisonEntityTypeChangeFilter(
  entityType: string,
  changeFilter: ComparisonChangeType | 'all' | 'actionable'
): void {
  const viewState = getComparisonEntityTypeState(entityType);
  viewState.changeFilter = changeFilter;
  if (changeFilter === 'all' || changeFilter === 'actionable') {
    viewState.showUnchanged = false;
  }
  renderComparisonEntityGroups();
}

export function setComparisonEntityTypeShowUnchanged(entityType: string, show: boolean): void {
  const viewState = getComparisonEntityTypeState(entityType);
  viewState.showUnchanged = show;
  renderComparisonEntityGroups();
}

export function getComparisonReportFilter(): ComparisonChangeType | 'all' {
  return state.comparison.reportFilter;
}

export function isValidComparisonReportFilter(value: string): value is ComparisonChangeType | 'all' {
  return REPORT_FILTER_OPTIONS.includes(value as ComparisonChangeType | 'all');
}

export function setComparisonReportFilter(filter: ComparisonChangeType | 'all'): void {
  state.comparison.reportFilter = filter;
  updateComparisonHash(state.comparison.activeReportId, filter);
  if (state.comparison.activeReportId && state.comparison.reportEntities) {
    renderComparisonSummary(state.comparison.activeReportId, state.comparison.reportEntities);
  }
  renderComparisonEntityGroups();
}

export function updateComparisonHash(
  reportId: string | null = state.comparison.activeReportId,
  filter: ComparisonChangeType | 'all' = state.comparison.reportFilter
): void {
  let currentHash = '';
  let locationExists = false;
  try {
    locationExists = typeof window !== 'undefined' && !!window.location;
    if (locationExists) currentHash = window.location.hash;
  } catch {
    return;
  }
  if (!locationExists) return;
  const params = new URLSearchParams();
  if (reportId) params.set('report', reportId);
  if (filter && filter !== 'all') params.set('filter', filter);
  const query = params.toString();
  const hash = query ? `#compare?${query}` : '#compare';
  if (currentHash !== hash) {
    window.location.hash = hash;
  }
}

export function parseComparisonHash(hash: string): { reportId: string | null; filter: ComparisonChangeType | 'all' } {
  const clean = hash.replace(/^#/, '');
  const [tabPart, queryPart] = clean.split('?');
  const params = new URLSearchParams(queryPart || '');
  const reportId = params.get('report');
  const filterValue = params.get('filter') || 'all';
  const filter = isValidComparisonReportFilter(filterValue) ? filterValue : 'all';
  return { reportId, filter };
}

export function getComparisonSideLabel(value: string): string {
  if (value === 'source') return 'Source (Live)';
  if (value === 'target') return 'Target (Live)';
  if (value.startsWith('package:')) {
    const packageId = value.slice('package:'.length);
    const pkg = state.staging.packages.find((p) => p.id === packageId);
    return `Package: ${pkg?.name || packageId}`;
  }
  return value;
}

function setComparisonLoading(loading: boolean): void {
  state.comparison.loading = loading;
  const loader = document.getElementById('comparison-loading-state');
  const summary = document.getElementById('comparison-summary');
  if (loader) loader.style.display = loading ? 'flex' : 'none';
  if (summary && !state.comparison.activeReportId) summary.style.display = loading ? 'none' : 'block';
}

export async function initComparison(): Promise<void> {
  await populateComparisonSideOptions();
  renderComparisonForm();
  await fetchComparisonReports();

  let hash = '';
  try {
    hash = typeof window !== 'undefined' && window.location ? window.location.hash : '';
  } catch {
    hash = '';
  }
  if (hash.startsWith('#compare')) {
    const { reportId, filter } = parseComparisonHash(hash);
    state.comparison.reportFilter = filter;
    if (reportId) {
      await loadComparisonReport(reportId, false);
    }
  }
}

export async function fetchComparisonReports(): Promise<void> {
  try {
    const res = await fetch('/api/v1/comparison/reports?limit=50');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.comparison.reports = (data.reports || []) as ComparisonReport[];
    renderComparisonReportsList();
  } catch (err: any) {
    showToast(`Failed to load comparison reports: ${err.message || String(err)}`, 'error', 4000);
  }
}

export function renderComparisonReportsList(): void {
  const container = document.getElementById('comparison-reports-list');
  if (!container) return;

  const reports = state.comparison.reports;
  if (reports.length === 0) {
    container.innerHTML = `
      <div class="empty-state-inline text-muted">
        No comparison reports yet. Pick two sides and run a comparison to generate a report.
      </div>
    `;
    return;
  }

  const rows = reports
    .map((report) => {
      const statusClass = getComparisonStatusBadgeClass(report.status);
      const summary = typeof report.summary === 'string' ? null : report.summary;
      const total = summary?.total_entities ?? 0;
      const caption = summary?.caption || `Status: ${report.status}`;
      const started = formatRelativeTime(report.started_at);
      const isActive = state.comparison.activeReportId === report.id;

      return `
        <div class="comparison-report-card ${isActive ? 'active' : ''}" data-id="${escapeHtml(report.id)}" role="button" tabindex="0">
          <div class="comparison-report-header">
            <span class="badge ${statusClass}">${escapeHtml(report.status)}</span>
            <span class="text-muted mono" style="font-size: 0.75rem;">${escapeHtml(report.id)}</span>
          </div>
          <div class="comparison-report-sides" style="font-size: 0.825rem; margin: 0.35rem 0;">
            <span>↔ ${escapeHtml(getComparisonSideLabel(report.left_side))}</span>
            <span style="margin: 0 0.35rem;">vs</span>
            <span>${escapeHtml(getComparisonSideLabel(report.right_side))}</span>
          </div>
          <div class="comparison-report-meta" style="font-size: 0.75rem; color: var(--text-secondary);">
            <span>${escapeHtml(caption)}</span>
            <span>${total} entities • ${escapeHtml(started)}</span>
          </div>
          <div class="comparison-report-actions" style="margin-top: 0.5rem; display: flex; gap: 0.5rem;">
            <button type="button" class="btn btn-xs btn-secondary btn-view-report" data-id="${escapeHtml(report.id)}">View Summary</button>
            <button type="button" class="btn btn-xs btn-outline-danger btn-delete-report" data-id="${escapeHtml(report.id)}">Delete</button>
          </div>
        </div>
      `;
    })
    .join('');

  container.innerHTML = rows;
}

function getComparisonStatusBadgeClass(status?: string): string {
  switch (status) {
    case 'completed':
      return 'badge-success';
    case 'running':
      return 'badge-accent';
    case 'failed':
      return 'badge-danger';
    case 'cancelled':
      return 'badge-warning';
    default:
      return 'badge-neutral';
  }
}

export async function populateComparisonSideOptions(): Promise<void> {
  try {
    const res = await fetch('/api/v1/staging/packages?limit=100');
    if (res.ok) {
      const data = await res.json();
      state.staging.packages = (data.packages || []) as typeof state.staging.packages;
    }
  } catch {
    // Packages are optional; source/target are always available.
  }
}

export function renderComparisonForm(): void {
  const leftSelect = document.getElementById('comparison-left-side') as HTMLSelectElement | null;
  const rightSelect = document.getElementById('comparison-right-side') as HTMLSelectElement | null;
  const entityTypesGrid = document.getElementById('comparison-entity-types-grid');

  const baseOptions = `
    <option value="source">Source (Live PIM)</option>
    <option value="target">Target (Live PIM)</option>
    <optgroup label="Staging Packages">
      ${state.staging.packages
        .map(
          (pkg) =>
            `<option value="package:${escapeHtml(pkg.id)}">${escapeHtml(pkg.name || pkg.id)}</option>`
        )
        .join('')}
    </optgroup>
  `;

  if (leftSelect) {
    const selected = state.comparison.leftSide;
    leftSelect.innerHTML = baseOptions;
    leftSelect.value = selected;
  }

  if (rightSelect) {
    const selected = state.comparison.rightSide;
    rightSelect.innerHTML = baseOptions;
    rightSelect.value = selected;
  }

  if (entityTypesGrid) {
    entityTypesGrid.innerHTML = state.comparison.entityTypes
      .map(
        (type) => `
        <label class="comparison-entity-type-card" data-entity-type="${escapeHtml(type)}">
          <input type="checkbox" class="comparison-entity-type-cb" value="${escapeHtml(type)}" checked />
          <span class="comparison-entity-type-label">${escapeHtml(formatEntityTypeLabel(type))}</span>
        </label>
      `
      )
      .join('');
  }

  updateComparisonRunButton();
}

function formatEntityTypeLabel(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatReportFilterLabel(filter: ComparisonChangeType | 'all'): string {
  if (filter === 'all') return 'All';
  return filter.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function setComparisonSide(side: 'left' | 'right', value: string): void {
  if (side === 'left') {
    state.comparison.leftSide = value;
  } else {
    state.comparison.rightSide = value;
  }
  updateComparisonRunButton();
}

export function toggleComparisonEntityType(entityType: string, checked: boolean): void {
  const index = state.comparison.entityTypes.indexOf(entityType);
  if (checked && index === -1) {
    state.comparison.entityTypes.push(entityType);
  } else if (!checked && index !== -1) {
    state.comparison.entityTypes.splice(index, 1);
  }
  updateComparisonRunButton();
}

export function selectAllComparisonEntityTypes(checked: boolean): void {
  state.comparison.entityTypes = checked ? [...COMPARISON_DEFAULT_ENTITY_TYPES] : [];
  document.querySelectorAll<HTMLInputElement>('.comparison-entity-type-cb').forEach((cb) => {
    cb.checked = checked;
    cb.closest('.comparison-entity-type-card')?.classList.toggle('active', checked);
  });
  updateComparisonRunButton();
}

function updateComparisonRunButton(): void {
  const btn = document.getElementById('comparison-run-btn') as HTMLButtonElement | null;
  if (!btn) return;
  const valid =
    state.comparison.leftSide &&
    state.comparison.rightSide &&
    state.comparison.leftSide !== state.comparison.rightSide &&
    state.comparison.entityTypes.length > 0;
  btn.disabled = !valid || state.comparison.loading;
}

export async function runComparison(): Promise<void> {
  if (state.comparison.leftSide === state.comparison.rightSide) {
    showToast('Left and right sides must be different', 'warning');
    return;
  }

  if (state.comparison.entityTypes.length === 0) {
    showToast('Select at least one entity type to compare', 'warning');
    return;
  }

  setComparisonLoading(true);
  updateComparisonRunButton();

  try {
    const res = await fetch('/api/v1/comparison/reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        left_side: state.comparison.leftSide,
        right_side: state.comparison.rightSide,
        entity_types: state.comparison.entityTypes,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const reportId = data.reportId as string;
    state.comparison.runningReportId = reportId;
    showToast('Comparison job started', 'success', 2000);

    await fetchComparisonReports();
    await pollReportUntilComplete(reportId);
  } catch (err: any) {
    showToast(`Comparison failed: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    setComparisonLoading(false);
    updateComparisonRunButton();
  }
}

async function pollReportUntilComplete(reportId: string): Promise<void> {
  let attempts = 0;
  const maxAttempts = 120;

  while (attempts < maxAttempts) {
    const report = await fetchComparisonReport(reportId, false);
    if (!report) break;

    if (report.status === 'completed') {
      state.comparison.runningReportId = null;
      state.comparison.activeReportId = reportId;
      await loadComparisonReportDetail(reportId);
      await fetchComparisonReports();
      showToast('Comparison completed', 'success', 3000);
      return;
    }

    if (report.status === 'failed' || report.status === 'cancelled') {
      state.comparison.runningReportId = null;
      await fetchComparisonReports();
      throw new Error(report.error_message || `Comparison ${report.status}`);
    }

    attempts++;
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }

  state.comparison.runningReportId = null;
  throw new Error('Comparison timed out');
}

async function fetchComparisonReport(reportId: string, showError = true): Promise<ComparisonReport | null> {
  try {
    const res = await fetch(`/api/v1/comparison/reports/${encodeURIComponent(reportId)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.report || null) as ComparisonReport | null;
  } catch (err: any) {
    if (showError) {
      showToast(`Failed to load report: ${err.message || String(err)}`, 'error', 4000);
    }
    return null;
  }
}

export async function loadComparisonReport(reportId: string, updateHash = true): Promise<void> {
  state.comparison.activeReportId = reportId;
  renderComparisonReportsList();
  await loadComparisonReportDetail(reportId);
  if (updateHash) {
    updateComparisonHash(reportId, state.comparison.reportFilter);
  }
}

async function loadComparisonReportDetail(reportId: string): Promise<void> {
  setComparisonLoading(true);
  try {
    const res = await fetch(`/api/v1/comparison/reports/${encodeURIComponent(reportId)}/entities?limit=10000`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const entities = (data.entities || []) as Array<{
      entity_type: string;
      entity_id: string;
      change_type: string;
      left_hash?: string | null;
      right_hash?: string | null;
    }>;

    state.comparison.reportEntities = entities;
    renderComparisonSummary(reportId, entities);
    renderComparisonEntityGroups();
  } catch (err: any) {
    showToast(`Failed to load report details: ${err.message || String(err)}`, 'error', 4000);
  } finally {
    setComparisonLoading(false);
  }
}

function computeTypeCounts(entities: ComparisonGroupedEntities): ComparisonTypeCount[] {
  const counts = new Map<string, ComparisonTypeCount>();

  for (const [entityType, items] of Object.entries(entities)) {
    const tally: ComparisonTypeCount = {
      entityType,
      added: 0,
      removed: 0,
      modified: 0,
      unchanged: 0,
      total: items.length,
    };
    for (const item of items) {
      if (item.changeType === 'added') tally.added++;
      else if (item.changeType === 'removed') tally.removed++;
      else if (item.changeType === 'modified') tally.modified++;
      else if (item.changeType === 'unchanged') tally.unchanged++;
    }
    counts.set(entityType, tally);
  }

  return Array.from(counts.values()).sort((a, b) => b.total - a.total);
}

export function renderComparisonSummary(
  reportId: string,
  entities: Array<{ entity_type: string; entity_id: string; change_type: string }>
): void {
  state.comparison.reportEntities = entities as any;
  const summaryContainer = document.getElementById('comparison-summary');
  const captionEl = document.getElementById('comparison-summary-caption');
  const reportTitle = document.getElementById('comparison-report-title');
  const emptyState = document.getElementById('comparison-empty-state');
  const legendContainer = document.getElementById('comparison-summary-legend');

  if (!summaryContainer) return;

  const report = state.comparison.reports.find((r) => r.id === reportId);
  const summary = typeof report?.summary === 'string' ? null : report?.summary;

  if (emptyState) emptyState.style.display = 'none';
  summaryContainer.style.display = 'block';

  if (reportTitle) {
    reportTitle.textContent = `Comparison Summary`;
    reportTitle.innerHTML = `Comparison Summary <span class="text-muted mono" style="font-size: 0.75rem;">${escapeHtml(reportId)}</span>`;
  }

  if (captionEl) {
    captionEl.textContent = summary?.caption || `${entities.length} entities compared`;
  }

  const filterContainer = document.getElementById('comparison-report-filter');
  if (filterContainer) {
    if (entities.length === 0) {
      filterContainer.style.display = 'none';
    } else {
      filterContainer.style.display = 'flex';
      const activeFilter = state.comparison.reportFilter;
      filterContainer.innerHTML = REPORT_FILTER_OPTIONS.map(
        (filter) => `
          <button
            type="button"
            class="filter-tab-btn ${activeFilter === filter ? 'active' : ''}"
            data-report-filter="${escapeHtml(filter)}"
            role="tab"
            aria-selected="${activeFilter === filter ? 'true' : 'false'}"
          >
            ${escapeHtml(formatReportFilterLabel(filter))}
          </button>
        `
      ).join('');
    }
  }

  if (legendContainer) {
    if (entities.length === 0) {
      legendContainer.style.display = 'none';
      legendContainer.innerHTML = '';
    } else {
      legendContainer.style.display = 'flex';
      legendContainer.innerHTML = `
        <span class="comparison-legend-title" style="font-size: 0.75rem; color: var(--text-secondary); margin-right: 0.5rem;">Legend:</span>
        ${CHANGE_TYPE_LEGEND.map(
          (entry) => `
            <span class="comparison-legend-item ${escapeHtml(entry.cls)}" style="font-size: 0.75rem; margin-right: 0.75rem; display: inline-flex; align-items: center; gap: 0.2rem;">
              <span class="comparison-legend-symbol" style="font-weight: 600;">${escapeHtml(entry.symbol)}</span>
              <span>${escapeHtml(entry.label)}</span>
            </span>
          `
        ).join('')}
      `;
    }
  }

  const grouped = groupEntitiesByType(entities);
  const typeCounts = computeTypeCounts(grouped);
  const cardsContainer = document.getElementById('comparison-type-cards');

  if (cardsContainer) {
    if (typeCounts.length === 0) {
      cardsContainer.innerHTML = `
        <div class="empty-state-inline text-muted">No entities found in this report.</div>
      `;
    } else {
      cardsContainer.innerHTML = typeCounts
        .map(
          (t) => {
            const viewState = getComparisonEntityTypeState(t.entityType);
            const perTypeFilter = viewState.changeFilter || 'actionable';
            const countsHtml = CHANGE_TYPE_LEGEND
              .map(
                (entry) => {
                  const countValue = t[entry.key];
                  const isActive = perTypeFilter === entry.key && state.comparison.reportFilter === 'all';
                  return `
                    <span
                      class="comparison-type-count ${escapeHtml(entry.cls)} ${isActive ? 'active' : ''}"
                      data-entity-type="${escapeHtml(t.entityType)}"
                      data-change-type="${escapeHtml(entry.key)}"
                      title="${escapeHtml(entry.label)} — click to filter"
                      role="button"
                      tabindex="0"
                      aria-label="${escapeHtml(entry.label)} ${countValue} for ${formatEntityTypeLabel(t.entityType)}"
                    >${escapeHtml(entry.symbol)}${countValue}</span>
                  `;
                }
              )
              .join('');
            return `
              <div class="comparison-type-card" data-entity-type="${escapeHtml(t.entityType)}" role="button" tabindex="0">
                <div class="comparison-type-card-header">
                  <span class="comparison-type-card-title">${escapeHtml(formatEntityTypeLabel(t.entityType))}</span>
                  <span class="badge badge-neutral">${t.total}</span>
                </div>
                <div class="comparison-type-counts">
                  ${countsHtml}
                </div>
                <div class="comparison-type-card-hint">Click counts to filter, or click card to expand</div>
              </div>
            `;
          }
        )
        .join('');
    }
  }
}

function groupEntitiesByType(
  entities: Array<{ entity_type: string; entity_id: string; change_type: string; left_hash?: string | null; right_hash?: string | null }>
): ComparisonGroupedEntities {
  const grouped: ComparisonGroupedEntities = {};
  for (const entity of entities) {
    if (!grouped[entity.entity_type]) grouped[entity.entity_type] = [];
    grouped[entity.entity_type].push({
      entityId: entity.entity_id,
      changeType: entity.change_type,
      leftHash: entity.left_hash,
      rightHash: entity.right_hash,
    });
  }
  return grouped;
}

export function renderComparisonEntityGroups(
  entities?: Array<{ entity_type: string; entity_id: string; change_type: string }>
): void {
  const container = document.getElementById('comparison-type-entities-list');
  if (!container) return;

  const allEntities = entities ?? state.comparison.reportEntities ?? [];
  state.comparison.reportEntities = allEntities;
  const grouped = groupEntitiesByType(allEntities);
  const typeCounts = computeTypeCounts(grouped);

  if (typeCounts.length === 0) {
    container.innerHTML = '';
    return;
  }

  const reportId = state.comparison.activeReportId;
  const activeEntityType = document.activeElement?.closest('.comparison-type-entities-group')?.getAttribute('data-entity-type') || null;
  const activeInputSelector =
    document.activeElement?.classList.contains('comparison-entity-search-input')
      ? '.comparison-entity-search-input'
      : null;
  const activeInputValue =
    document.activeElement instanceof HTMLInputElement
      ? (document.activeElement as HTMLInputElement).value
      : '';
  const selectionStart =
    document.activeElement instanceof HTMLInputElement
      ? (document.activeElement as HTMLInputElement).selectionStart
      : null;
  const selectionEnd =
    document.activeElement instanceof HTMLInputElement
      ? (document.activeElement as HTMLInputElement).selectionEnd
      : null;

  container.innerHTML = typeCounts
    .map((type) => {
      const isExpanded = state.comparison.expandedTypes.has(type.entityType);
      const items = grouped[type.entityType] || [];
      const viewState = getComparisonEntityTypeState(type.entityType);
      const entityFilter = viewState.filter.trim().toLowerCase();
      const reportFilter = state.comparison.reportFilter;
      const perTypeFilter = viewState.changeFilter || 'actionable';
      const showUnchanged = viewState.showUnchanged ?? false;

      let filtered = items;
      if (reportFilter !== 'all') {
        filtered = filtered.filter((item) => item.changeType === reportFilter);
      } else {
        const matchesPerTypeFilter = (changeType: string) => {
          if (perTypeFilter === 'all') return true;
          if (perTypeFilter === 'actionable') return changeType !== 'unchanged';
          return changeType === perTypeFilter;
        };
        filtered = filtered.filter((item) => {
          const base = matchesPerTypeFilter(item.changeType);
          if (showUnchanged && item.changeType === 'unchanged') return true;
          return base;
        });
      }
      if (entityFilter) {
        filtered = filtered.filter((item) => item.entityId.toLowerCase().includes(entityFilter));
      }
      const pageSize = viewState.pageSize;
      const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
      const page = Math.min(Math.max(1, viewState.page), totalPages);
      viewState.page = page;
      const start = (page - 1) * pageSize;
      const pageItems = filtered.slice(start, start + pageSize);

      const rows = pageItems
        .map(
          (item) => `
          <div class="comparison-entity-row" data-change-type="${escapeHtml(item.changeType)}">
            <span class="comparison-entity-id mono">${escapeHtml(item.entityId)}</span>
            <span class="comparison-change-badge ${getChangeBadgeClass(item.changeType)}">${escapeHtml(item.changeType)}</span>
            ${
              reportId && item.changeType !== 'unchanged'
                ? `<button type="button" class="btn btn-xs btn-secondary btn-view-entity-diff" data-report-id="${escapeHtml(reportId)}" data-entity-type="${escapeHtml(type.entityType)}" data-entity-id="${escapeHtml(item.entityId)}">View Diff</button>`
                : ''
            }
          </div>
        `
        )
        .join('');

      const totalCount = filtered.length;
      const showingStart = totalCount === 0 ? 0 : start + 1;
      const showingEnd = Math.min(start + pageSize, totalCount);

      const activeFilterLabel =
        reportFilter !== 'all'
          ? `Report filter: ${formatChangeTypeLabel(reportFilter)}`
          : perTypeFilter !== 'actionable' || showUnchanged
          ? `Showing: ${formatChangeTypeLabel(perTypeFilter)}${showUnchanged ? ' + Unchanged' : ''}`
          : '';

      const toolbar = `
        <div class="comparison-entity-toolbar" style="display: flex; gap: 0.5rem; align-items: center; padding: 0.5rem 0; flex-wrap: wrap;">
          <input
            type="search"
            class="comparison-entity-search-input form-control"
            data-entity-type="${escapeHtml(type.entityType)}"
            placeholder="Filter by entity id/code..."
            value="${escapeHtml(viewState.filter)}"
            style="flex: 1; min-width: 8rem;"
          />
          <select class="comparison-page-size form-control" data-entity-type="${escapeHtml(type.entityType)}" style="width: auto;">
            ${renderComparisonPageSizeOptions(pageSize)}
          </select>
          <label class="comparison-show-unchanged-label" style="font-size: 0.75rem; display: inline-flex; align-items: center; gap: 0.25rem; cursor: pointer; user-select: none;">
            <input
              type="checkbox"
              class="comparison-show-unchanged-toggle"
              data-entity-type="${escapeHtml(type.entityType)}"
              ${showUnchanged ? 'checked' : ''}
            />
            Include unchanged
          </label>
        </div>
        ${activeFilterLabel ? `<div class="comparison-active-filter-label" style="font-size: 0.75rem; color: var(--text-secondary); padding: 0 0 0.35rem;">${escapeHtml(activeFilterLabel)}</div>` : ''}
      `;

      const pagination =
        totalPages > 1
          ? renderComparisonPagination(type.entityType, page, totalPages, totalCount, showingStart, showingEnd)
          : totalCount > 0
          ? `<div class="comparison-pagination-info text-muted" style="font-size: 0.75rem; padding: 0.35rem 0;">Showing ${showingStart}-${showingEnd} of ${totalCount}</div>`
          : '';

      return `
        <div class="comparison-type-entities-group ${isExpanded ? 'expanded' : ''}" data-entity-type="${escapeHtml(type.entityType)}">
          <div class="comparison-type-entities-header" role="button" tabindex="0">
            <span class="comparison-type-entities-title">${escapeHtml(formatEntityTypeLabel(type.entityType))}</span>
            <span class="badge badge-neutral">${items.length}</span>
            <span class="comparison-expand-icon">${isExpanded ? '▼' : '▶'}</span>
          </div>
          <div class="comparison-type-entities-body" style="display: ${isExpanded ? 'block' : 'none'};">
            ${toolbar}
            <div class="comparison-entity-rows">
              ${rows || '<div class="text-muted" style="padding: 0.5rem;">No entities</div>'}
            </div>
            ${pagination}
          </div>
        </div>
      `;
    })
    .join('');

  // Restore focus to the search input that the user was typing in, if it still
  // exists after re-render. We must do this because renderComparisonEntityGroups
  // rebuilds the entire list DOM on every keystroke.
  if (activeEntityType && activeInputSelector) {
    const group = container.querySelector(`.comparison-type-entities-group[data-entity-type="${escapeCssSelector(activeEntityType)}"]`);
    if (group) {
      const input = group.querySelector(activeInputSelector) as HTMLInputElement | null;
      if (input) {
        input.focus();
        if (activeInputValue !== '' &&
          document.activeElement === input &&
          input.value === activeInputValue) {
          input.setSelectionRange(selectionStart ?? activeInputValue.length, selectionEnd ?? activeInputValue.length);
        }
      }
    }
  }
}

function getComparisonEntityTypeState(entityType: string): ComparisonEntityTypeViewState {
  let viewState = state.comparison.entityTypeStates.get(entityType);
  if (!viewState) {
    viewState = getDefaultComparisonEntityTypeViewState();
    state.comparison.entityTypeStates.set(entityType, viewState);
  }
  return viewState;
}

function renderComparisonPageSizeOptions(selectedPageSize: number): string {
  return [25, 50, 100, 200]
    .map(
      (size) =>
        `<option value="${size}" ${size === selectedPageSize ? 'selected' : ''}>${size} / page</option>`
    )
    .join('');
}

function renderComparisonPagination(
  entityType: string,
  page: number,
  totalPages: number,
  totalCount: number,
  showingStart: number,
  showingEnd: number
): string {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .map(
      (p) =>
        `<button type="button" class="btn btn-xs comparison-pagination-btn ${p === page ? 'btn-primary' : 'btn-outline'}" data-entity-type="${escapeHtml(entityType)}" data-action="page" data-page="${p}">${p}</button>`
    )
    .join('');

  return `
    <div class="comparison-pagination" style="display: flex; gap: 0.35rem; align-items: center; flex-wrap: wrap; padding: 0.5rem 0;">
      <button type="button" class="btn btn-xs btn-secondary comparison-pagination-btn" data-entity-type="${escapeHtml(entityType)}" data-action="prev" ${page === 1 ? 'disabled' : ''}>Prev</button>
      ${pages}
      <button type="button" class="btn btn-xs btn-secondary comparison-pagination-btn" data-entity-type="${escapeHtml(entityType)}" data-action="next" ${page === totalPages ? 'disabled' : ''}>Next</button>
      <span class="comparison-pagination-info text-muted" style="font-size: 0.75rem; margin-left: 0.35rem;">Page ${page} of ${totalPages} • ${showingStart}-${showingEnd} of ${totalCount}</span>
    </div>
  `;
}

export function setComparisonEntityTypeFilter(entityType: string, filter: string): void {
  const viewState = getComparisonEntityTypeState(entityType);
  viewState.filter = filter;
  viewState.page = 1;
  renderComparisonEntityGroups();
}

export function setComparisonEntityTypePage(entityType: string, page: number): void {
  const viewState = getComparisonEntityTypeState(entityType);
  viewState.page = page;
  renderComparisonEntityGroups();
}

export function setComparisonEntityTypePageSize(entityType: string, pageSize: number): void {
  const viewState = getComparisonEntityTypeState(entityType);
  viewState.pageSize = pageSize;
  viewState.page = 1;
  renderComparisonEntityGroups();
}

function getChangeBadgeClass(changeType: string): string {
  switch (changeType) {
    case 'added':
      return 'badge-success';
    case 'removed':
      return 'badge-danger';
    case 'modified':
      return 'badge-accent';
    default:
      return 'badge-neutral';
  }
}

export function toggleComparisonTypeExpansion(entityType: string): void {
  const expanded = state.comparison.expandedTypes.has(entityType);
  if (expanded) {
    state.comparison.expandedTypes.delete(entityType);
  } else {
    state.comparison.expandedTypes.add(entityType);
  }

  const group = document.querySelector(`.comparison-type-entities-group[data-entity-type="${escapeCssSelector(entityType)}"]`);
  if (group) {
    group.classList.toggle('expanded', !expanded);
    const body = group.querySelector('.comparison-type-entities-body') as HTMLElement | null;
    const icon = group.querySelector('.comparison-expand-icon');
    if (body) body.style.display = expanded ? 'none' : 'block';
    if (icon) icon.textContent = expanded ? '▶' : '▼';
  }
}

export async function openComparisonEntityDiff(
  reportId: string,
  entityType: string,
  entityId: string
): Promise<void> {
  state.comparison.diffLoading = true;
  state.comparison.selectedEntity = { entityType, entityId };
  state.comparison.diffModal = null;

  // Close any explorer diff state to avoid event-handler confusion.
  state.explorer.diffModal = null;

  try {
    const [reportRes, diffRes] = await Promise.all([
      fetch(`/api/v1/comparison/reports/${encodeURIComponent(reportId)}`),
      fetch(
        `/api/v1/comparison/reports/${encodeURIComponent(reportId)}/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/diff`
      ),
    ]);

    if (!reportRes.ok) {
      const errData = await reportRes.json().catch(() => ({}));
      throw new Error(errData.message || `Report HTTP ${reportRes.status}`);
    }
    if (!diffRes.ok) {
      const errData = await diffRes.json().catch(() => ({}));
      throw new Error(errData.message || `Diff HTTP ${diffRes.status}`);
    }

    const reportData = await reportRes.json();
    const diffData = await diffRes.json();

    const report = reportData.report as ComparisonReport | undefined;
    const entity = diffData.entity as {
      diff?: Record<string, unknown> | null;
    } | null;

    const rawDiff = entity?.diff || (diffData.diff as Record<string, unknown>) || {};
    const changes = normalizeComparisonDiff(rawDiff);

    state.comparison.diffModal = {
      reportId,
      entityType,
      entityId,
      leftSide: report?.left_side || 'source',
      rightSide: report?.right_side || 'target',
      leftLabel: getComparisonSideLabel(report?.left_side || 'source'),
      rightLabel: getComparisonSideLabel(report?.right_side || 'target'),
      leftPayload: buildPayloadFromChanges(changes, 'left'),
      rightPayload: buildPayloadFromChanges(changes, 'right'),
      diff: rawDiff,
      changes,
      changeType: String(rawDiff?.changeType || 'unchanged'),
      loading: false,
      error: null,
      syncing: false,
      addingToPackage: false,
    };

    const modal = document.getElementById('entity-diff-modal');
    if (modal) modal.style.display = 'flex';
    renderComparisonDiffModal();
  } catch (err: any) {
    state.comparison.diffModal = {
      reportId,
      entityType,
      entityId,
      leftSide: 'source',
      rightSide: 'target',
      leftLabel: getComparisonSideLabel('source'),
      rightLabel: getComparisonSideLabel('target'),
      leftPayload: null,
      rightPayload: null,
      diff: {},
      changes: [],
      changeType: 'unchanged',
      loading: false,
      error: err.message || String(err),
      syncing: false,
      addingToPackage: false,
    };
    const modal = document.getElementById('entity-diff-modal');
    if (modal) modal.style.display = 'flex';
    renderComparisonDiffModal();
    showToast(`Failed to load diff: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    state.comparison.diffLoading = false;
  }
}

export function renderComparisonDiffModal(): void {
  const modal = document.getElementById('entity-diff-modal');
  const title = document.getElementById('entity-diff-title');
  const subtitle = document.getElementById('entity-diff-subtitle');
  const statusBadges = document.getElementById('entity-diff-status-badges');
  const summary = document.getElementById('entity-diff-summary');
  const fieldDiffContainer = document.getElementById('entity-diff-field-list');
  const sourceJson = document.getElementById('entity-diff-source-json');
  const targetJson = document.getElementById('entity-diff-target-json');
  const spinner = document.getElementById('entity-diff-loading');
  const depsPanel = document.getElementById('entity-diff-deps-panel');
  const syncRightBtn = document.getElementById('btn-comparison-diff-sync-right') as HTMLButtonElement | null;
  const addToPackageBtn = document.getElementById('btn-comparison-diff-add-to-package') as HTMLButtonElement | null;
  const explorerSyncBtn = document.getElementById('btn-entity-diff-sync-now') as HTMLButtonElement | null;
  const cascadeSyncBtn = document.getElementById('btn-cascade-sync-diff') as HTMLButtonElement | null;

  if (!modal) return;

  const diff = state.comparison.diffModal;
  if (!diff) {
    modal.style.display = 'none';
    return;
  }

  // Hide explorer-specific prerequisite panel when showing a comparison diff.
  if (depsPanel) depsPanel.style.display = 'none';

  // Show comparison action buttons; hide explorer-only sync buttons.
  if (syncRightBtn) syncRightBtn.style.display = 'inline-flex';
  if (addToPackageBtn) addToPackageBtn.style.display = 'inline-flex';
  if (explorerSyncBtn) explorerSyncBtn.style.display = 'none';
  if (cascadeSyncBtn) cascadeSyncBtn.style.display = 'none';

  const entityTypeLabel = formatEntityTypeLabel(diff.entityType);

  if (title) {
    title.textContent = `Comparison Diff: ${entityTypeLabel}`;
  }
  if (subtitle) {
    subtitle.textContent = `${escapeHtml(diff.entityId)} • ${escapeHtml(diff.leftLabel)} vs ${escapeHtml(diff.rightLabel)}`;
  }

  if (statusBadges) {
    const changeType = diff.changeType || 'unchanged';
    const leftBadge = diff.leftPayload !== null && diff.leftPayload !== undefined
      ? '<span class="badge badge-success">Left Exists</span>'
      : '<span class="badge badge-neutral">Left Missing</span>';
    const rightBadge = diff.rightPayload !== null && diff.rightPayload !== undefined
      ? '<span class="badge badge-info">Right Exists</span>'
      : '<span class="badge badge-warning">Right Missing</span>';
    const changeBadge = diff.changes.length === 0 && changeType === 'unchanged'
      ? '<span class="badge badge-success">Identical</span>'
      : `<span class="badge badge-accent">${escapeHtml(changeType)}</span>`;
    statusBadges.innerHTML = `${leftBadge} ${rightBadge} ${changeBadge}`;
  }

  if (summary) {
    const added = diff.changes.filter((c) => c.type === 'added').length;
    const removed = diff.changes.filter((c) => c.type === 'removed').length;
    const modified = diff.changes.filter((c) => c.type === 'modified').length;
    summary.innerHTML = `
      <div class="diff-summary-item diff-added">
        <span class="diff-summary-num">${added}</span>
        <span class="diff-summary-label">Added</span>
      </div>
      <div class="diff-summary-item diff-removed">
        <span class="diff-summary-num">${removed}</span>
        <span class="diff-summary-label">Removed</span>
      </div>
      <div class="diff-summary-item diff-modified">
        <span class="diff-summary-num">${modified}</span>
        <span class="diff-summary-label">Modified</span>
      </div>
      <div class="diff-summary-item">
        <span class="diff-summary-num">${diff.changes.length}</span>
        <span class="diff-summary-label">Total Changes</span>
      </div>
    `;
  }

  if (spinner) spinner.style.display = diff.loading ? 'flex' : 'none';

  if (fieldDiffContainer) {
    if (diff.changes.length === 0) {
      fieldDiffContainer.innerHTML = `
        <div class="empty-table-state" style="padding: 1.5rem;">
          <div class="empty-icon">✅</div>
          <h4>No Field Differences</h4>
          <p>Left and right payloads are identical for this entity.</p>
        </div>
      `;
    } else {
      const rows = diff.changes
        .map(
          (change) => `
            <div class="diff-field-row ${getDiffTypeClass(change.type)}">
              <div class="diff-field-path mono">${escapeHtml(change.path)}</div>
              <div class="diff-field-values">
                <div class="diff-field-value diff-source-value">
                  <span class="diff-field-label">Left (${escapeHtml(diff.leftLabel)})</span>
                  <pre class="diff-value-pre"><code>${formatDiffValue(change.newValue)}</code></pre>
                </div>
                <div class="diff-field-value diff-target-value">
                  <span class="diff-field-label">Right (${escapeHtml(diff.rightLabel)})</span>
                  <pre class="diff-value-pre"><code>${formatDiffValue(change.oldValue)}</code></pre>
                </div>
              </div>
              <div class="diff-field-status">
                <span class="diff-status-badge ${getDiffTypeClass(change.type)}">${getDiffTypeLabel(change.type)}</span>
              </div>
            </div>
          `
        )
        .join('');
      fieldDiffContainer.innerHTML = rows;
    }
  }

  if (sourceJson) {
    sourceJson.innerHTML = diff.leftPayload !== undefined && diff.leftPayload !== null
      ? `<code>${escapeHtml(JSON.stringify(diff.leftPayload, null, 2))}</code>`
      : `<code class="text-secondary">Left payload not available</code>`;
  }

  if (targetJson) {
    targetJson.innerHTML = diff.rightPayload !== undefined && diff.rightPayload !== null
      ? `<code>${escapeHtml(JSON.stringify(diff.rightPayload, null, 2))}</code>`
      : `<code class="text-secondary">Right payload not available</code>`;
  }

  const leftBadge = document.getElementById('entity-diff-left-badge');
  const rightBadge = document.getElementById('entity-diff-right-badge');
  if (leftBadge) leftBadge.textContent = diff.leftLabel;
  if (rightBadge) rightBadge.textContent = diff.rightLabel;

  const leftRaw = document.getElementById('entity-diff-left-raw') as HTMLDetailsElement | null;
  const rightRaw = document.getElementById('entity-diff-right-raw') as HTMLDetailsElement | null;
  if (leftRaw) leftRaw.open = true;
  if (rightRaw) rightRaw.open = true;

  const rightIsLive = diff.rightSide === 'source' || diff.rightSide === 'target';
  if (syncRightBtn) {
    syncRightBtn.disabled = diff.loading || diff.syncing || !rightIsLive;
    syncRightBtn.innerHTML = diff.syncing
      ? '<span class="spinner-sm"></span> Syncing...'
      : `<span>⚡ Sync Left → Right (${escapeHtml(diff.rightLabel)})</span>`;
  }
  if (addToPackageBtn) {
    addToPackageBtn.disabled = diff.loading || diff.addingToPackage;
    addToPackageBtn.innerHTML = diff.addingToPackage
      ? '<span class="spinner-sm"></span> Opening...'
      : '<span>📦 Add Left to Staging Package</span>';
  }
}

export function closeComparisonDiffModal(): void {
  const modal = document.getElementById('entity-diff-modal');
  if (modal) modal.style.display = 'none';
  state.comparison.diffModal = null;

  // Restore explorer sync buttons visibility so subsequent explorer diffs render correctly.
  const explorerSyncBtn = document.getElementById('btn-entity-diff-sync-now') as HTMLButtonElement | null;
  const cascadeSyncBtn = document.getElementById('btn-cascade-sync-diff') as HTMLButtonElement | null;
  const syncRightBtn = document.getElementById('btn-comparison-diff-sync-right') as HTMLButtonElement | null;
  const addToPackageBtn = document.getElementById('btn-comparison-diff-add-to-package') as HTMLButtonElement | null;
  const depsPanel = document.getElementById('entity-diff-deps-panel');
  if (explorerSyncBtn) explorerSyncBtn.style.display = 'inline-flex';
  if (cascadeSyncBtn) cascadeSyncBtn.style.display = 'inline-flex';
  if (syncRightBtn) syncRightBtn.style.display = 'none';
  if (addToPackageBtn) addToPackageBtn.style.display = 'none';
  if (depsPanel) depsPanel.style.display = 'block';

  const leftBadge = document.getElementById('entity-diff-left-badge');
  const rightBadge = document.getElementById('entity-diff-right-badge');
  if (leftBadge) leftBadge.textContent = 'Left';
  if (rightBadge) rightBadge.textContent = 'Right';
  const leftRaw = document.getElementById('entity-diff-left-raw') as HTMLDetailsElement | null;
  const rightRaw = document.getElementById('entity-diff-right-raw') as HTMLDetailsElement | null;
  if (leftRaw) leftRaw.open = true;
  if (rightRaw) rightRaw.open = true;
}

export async function executeComparisonDiffSync(): Promise<void> {
  const diff = state.comparison.diffModal;
  if (!diff) return;

  if (diff.rightSide !== 'source' && diff.rightSide !== 'target') {
    showToast('Sync is only available when the right side is a live PIM connection', 'warning');
    return;
  }

  state.comparison.diffModal = { ...diff, syncing: true };
  renderComparisonDiffModal();

  try {
    const customOptions: Record<string, any> = {
      items: [diff.entityId],
      entity_types: [diff.entityType],
      cascade: true,
      strategy: 'lean',
      dry_run: false,
      async: true,
    };

    if (diff.rightSide === 'source') {
      customOptions.source_connection_id = 'target';
      customOptions.target_connection_id = 'source';
    }

    await executeBatchSyncJob(false, customOptions);
    showToast(`Entity ${diff.entityId} queued for sync to ${diff.rightLabel}`, 'success', 3000);
  } catch (err: any) {
    showToast(`Sync failed: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    if (state.comparison.diffModal) {
      state.comparison.diffModal = { ...state.comparison.diffModal, syncing: false };
      renderComparisonDiffModal();
    }
  }
}

export async function openComparisonEntityAddToPackage(): Promise<void> {
  const diff = state.comparison.diffModal;
  if (!diff) return;

  state.comparison.diffModal = { ...diff, addingToPackage: true };
  renderComparisonDiffModal();

  try {
    await openAddToVirtualTargetModal([{ entity_type: diff.entityType, entity_id: diff.entityId }]);
  } catch (err: any) {
    showToast(`Failed to open package picker: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    if (state.comparison.diffModal) {
      state.comparison.diffModal = { ...state.comparison.diffModal, addingToPackage: false };
      renderComparisonDiffModal();
    }
  }
}

function buildPayloadFromChanges(
  changes: EntityDiffChange[],
  side: 'left' | 'right'
): Record<string, unknown> | null {
  if (changes.length === 0) return null;

  const root: Record<string, unknown> = {};
  for (const change of changes) {
    const value = side === 'left' ? change.newValue : change.oldValue;
    if (value === undefined) continue;
    setValueAtPath(root, change.path, value);
  }
  return root;
}

function setValueAtPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  if (!path) return;
  const keys = path.split('.');
  let current: any = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
}

function normalizeComparisonDiff(rawDiff: Record<string, unknown>): EntityDiffChange[] {
  const changes = rawDiff?.changes;
  if (Array.isArray(changes)) {
    return changes
      .filter((c: any) => c && typeof c === 'object')
      .map((c: any) => ({
        path: String(c.path || c.field || 'change'),
        type: mapComparisonChangeType(String(c.type || c.changeType)),
        oldValue: c.oldValue ?? c.old ?? c.previous ?? undefined,
        newValue: c.newValue ?? c.new ?? c.current ?? undefined,
      }));
  }

  if (rawDiff?.hasChanges) {
    return [
      {
        path: 'summary',
        type: 'modified',
        oldValue: undefined,
        newValue: rawDiff,
      },
    ];
  }

  return [];
}

function mapComparisonChangeType(type?: string): EntityDiffChange['type'] {
  switch (type?.toLowerCase()) {
    case 'added':
    case 'created':
      return 'added';
    case 'removed':
    case 'deleted':
      return 'removed';
    case 'modified':
    case 'updated':
      return 'modified';
    default:
      return 'modified';
  }
}

export async function deleteComparisonReport(reportId: string): Promise<void> {
  if (!confirm(`Delete comparison report ${reportId}?`)) return;

  try {
    const res = await fetch(`/api/v1/comparison/reports/${encodeURIComponent(reportId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    if (state.comparison.activeReportId === reportId) {
      state.comparison.activeReportId = null;
    }
    await fetchComparisonReports();
    renderComparisonSummary('', []);

    const summaryContainer = document.getElementById('comparison-summary');
    const emptyState = document.getElementById('comparison-empty-state');
    if (summaryContainer) summaryContainer.style.display = 'none';
    if (emptyState) emptyState.style.display = 'block';

    showToast('Comparison report deleted', 'success', 2000);
  } catch (err: any) {
    showToast(`Delete failed: ${err.message || String(err)}`, 'error', 4000);
  }
}
